import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from 'react-apollo';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';
import produce from 'immer';

import FormGroup from '../../../../../../components/global/FormGroup';
import Button from '../../../../../../components/global/Button';
import Spinner from '../../../../../../components/global/Spinner';
import SearchIcon from '../../../../../../components/icons/SearchIcon';
import { MeWithRosterQrCodeBlanks } from '../../../../../../graphql/queries/account';
import { rosterQrCodeUpdate as rosterQrCodeUpdateMutation } from '../../../../../../graphql/mutations/roster-identity';
import { AlbumFolderRosterIdentityBaseDetails } from '../../../../../../graphql/fragments/roster-identity';
import useGlobalToast from '../../../../../../hooks/useGlobalToast';

const QR_CODE_IDENTIFIER_TIP =
  'The QR Code identifier is the number read at the top left of the QR flyer.';

const propTypes = {
  albumFolderId: PropTypes.string.isRequired,
  rosterIdentityId: PropTypes.string.isRequired,
  rosterIdentityLinkId: PropTypes.string.isRequired,
  onCancel: PropTypes.func,
  onComplete: PropTypes.func,
};

const RosterIdentityRosterQrAssignment = ({
  albumFolderId,
  rosterIdentityId,
  rosterIdentityLinkId,
  onCancel,
  onComplete,
}) => {
  const [identifier, setIdentifier] = useState('');
  const [searchSequenceNumber, setSearchSequenceNumber] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const { setToast } = useGlobalToast();
  const [rosterQrCodeUpdate] = useMutation(rosterQrCodeUpdateMutation);

  const { data: { me } = {}, loading } = useQuery(MeWithRosterQrCodeBlanks, {
    variables: {
      album_folder_id: albumFolderId,
      sequence_number: searchSequenceNumber,
      include_roster_qr_data: true,
    },
    skip: searchSequenceNumber == null,
    fetchPolicy: 'network-only',
  });

  const matchingBlank = me?.roster_qr_codes?.records?.[0];
  const hasSearched = searchSequenceNumber != null && !loading;

  const onSearch = event => {
    event.preventDefault();

    const parsed = parseInt(identifier, 10);

    setSearchSequenceNumber(Number.isNaN(parsed) ? null : parsed);
  };

  const onConnect = async () => {
    if (!matchingBlank?.id) {
      return;
    }

    setConnecting(true);

    try {
      await rosterQrCodeUpdate({
        variables: {
          input: {
            roster_qr_code_id: matchingBlank.id,
            roster_identity_link_id: rosterIdentityLinkId,
          },
        },
        // Apollo's automatic normalization does not reliably propagate the
        // new QR code onto the roster identity link node that drives the
        // "Add Individual QR Code" action, so we explicitly patch the link in
        // the cache. We reuse the link returned by the mutation and pass the
        // simplified roster_qr_code { id } so it matches the
        // RosterIdentityLinkFields fragment selection.
        update: (store, { data }) => {
          const rosterQrCodeId = data?.rosterQrCodeUpdate?.roster_qr_code?.id;

          try {
            const rosterIdentityFragment = store.readFragment({
              id: defaultDataIdFromObject({
                id: rosterIdentityId,
                __typename: 'RosterIdentity',
              }),
              fragment: AlbumFolderRosterIdentityBaseDetails,
              fragmentName: 'AlbumFolderRosterIdentityBaseDetails',
              variables: {
                id: albumFolderId,
              },
            });

            const updatedFragment = produce(rosterIdentityFragment, draft => {
              draft.links = {
                ...draft.links,
                records: draft.links.records.map(r => {
                  if (r.id !== rosterIdentityLinkId) {
                    return r;
                  }

                  return {
                    ...r,
                    roster_qr_code: {
                      __typename: 'RosterQrCode',
                      id: rosterQrCodeId,
                    },
                  };
                }),
              };
            });

            store.writeFragment({
              id: defaultDataIdFromObject({
                id: rosterIdentityId,
                __typename: 'RosterIdentity',
              }),
              fragment: AlbumFolderRosterIdentityBaseDetails,
              fragmentName: 'AlbumFolderRosterIdentityBaseDetails',
              variables: {
                id: albumFolderId,
              },
              data: updatedFragment,
            });
          } catch {
            // Link may not be in the cache; nothing to update.
          }
        },
      });

      setToast('QR code connected successfully!');
      onComplete?.(matchingBlank, rosterIdentityLinkId);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Fragment>
      <h4 className=" text-center mb-2">Add Individual QR Code</h4>

      <form className="roster-qr-assignment-search" onSubmit={onSearch}>
        <FormGroup
          id="qr-code-identifier"
          className="m-0"
          type="number"
          label="QR Code Identifier"
          labelTip={QR_CODE_IDENTIFIER_TIP}
          placeholder="Enter the QR code identifier..."
          value={identifier}
          onChange={event => setIdentifier(event.target.value)}
        />
        <Button
          type="submit"
          className="roster-qr-assignment-search__button"
          disabled={!identifier || loading}
          aria-label="Search"
        >
          <SearchIcon size={16} color="#fff" className="mr-1" /> Search
        </Button>
      </form>

      {loading && (
        <div className="text-center mt-2">
          <Spinner />
        </div>
      )}

      {hasSearched && !matchingBlank && (
        <p className="text-danger text-center mt-2">
          No available QR code blank was found for this identifier.
        </p>
      )}

      {hasSearched && matchingBlank && (
        <div className="mt-2">
          <p className="text-success text-center">
            Found QR code blank #{matchingBlank.sequence_number}.
          </p>

          <Button btnBlock disabled={connecting} onClick={onConnect}>
            {connecting ? 'Connecting...' : 'Connect QR Code'}
          </Button>
        </div>
      )}

      <Button
        btnStyle="link"
        className="text-center mt-2"
        btnBlock
        onClick={() => onCancel?.()}
      >
        Cancel
      </Button>
    </Fragment>
  );
};

RosterIdentityRosterQrAssignment.propTypes = propTypes;

export default RosterIdentityRosterQrAssignment;
